import { getRuntimeConfig } from "@/app/config/runtime-config";
import {
  clearRegistrationFinancialToken,
  readRegistrationFinancialToken,
} from "./registration-financial-token-holder";

/**
 * Thin token-authenticated client over the CLMS-117 registration-financial
 * contract (`specs/006-6-registration-payment-handoff/contracts/
 * registration-financial-api-contract.md`), served by
 * `clm-web-service-financial` on the standard `apiBaseUrls.financial` base.
 * Authentication (FR-046): every call presents the handoff-scoped access
 * token from the module-memory holder as a bearer credential; with no held
 * token the API is never called, and a 401 scrubs the held token so the
 * flow reverts to the static pending surface (no refresh loop). Responses
 * are BOUNDARY data: every payload is parsed into an explicit internal
 * shape here and malformed payloads are rejected, so the rendering layer
 * never branches on unparsed provider/service JSON. The PaymentIntent
 * client secret and the access token only ever live in memory — never
 * logged, persisted, placed in a URL, or embedded in thrown errors.
 */

export const REGISTRATION_FINANCIAL_STATES = [
  "pending",
  "awaiting_payment",
  "completed",
  "actionable_failure",
  "deferred_no_action",
] as const;

export type RegistrationFinancialState = (typeof REGISTRATION_FINANCIAL_STATES)[number];

/** Closed user-safe outcome-category allowlist from the owner-result contract. */
export const REGISTRATION_FINANCIAL_OUTCOME_CATEGORIES = [
  "paid_activated",
  "company_covered_activated",
  "comp_activated",
  "nothing_owed_activated",
  "payment_retry_available",
  "payment_deferred",
  "support_required",
  "no_approved_subscription_path",
  "company_subscription_lapsed",
] as const;

export type RegistrationFinancialOutcomeCategory =
  (typeof REGISTRATION_FINANCIAL_OUTCOME_CATEGORIES)[number];

export interface RegistrationFinancialPaymentOffer {
  /** Server-resolved display amount; the client never computes or sends one. */
  amountDue: string;
  /**
   * Present only when the server offers checkout to the owning session. A
   * payment object WITHOUT a client secret (provider-pending / awaiting
   * fulfillment) MUST NOT render the payment step (FR-040).
   */
  paymentIntentClientSecret: string | null;
  /** Provider-pending window (e.g. ACH debit processing) — no retry, no secret. */
  processing: boolean;
  retryAvailable: boolean;
}

export interface RegistrationFinancialStatus {
  handoffId: string;
  state: RegistrationFinancialState;
  outcomeCategory: RegistrationFinancialOutcomeCategory | null;
  payment: RegistrationFinancialPaymentOffer | null;
  supportCorrelation: string | null;
}

export interface ReceiptCopyPreference {
  email: string | null;
}

/** User-safe receipt-copy mutation outcomes (server validation is authoritative). */
export type ReceiptCopySetResult =
  | { outcome: "saved"; preference: ReceiptCopyPreference }
  | { outcome: "rejected"; reason: "invalid_address" | "no_longer_editable" };

export type ReceiptCopyClearResult =
  | { outcome: "cleared" }
  | { outcome: "rejected"; reason: "no_longer_editable" };

export class RegistrationFinancialApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "RegistrationFinancialApiError";
    this.status = status;
  }
}

/**
 * FR-046: a 401 means the handoff token is missing, expired, or rejected —
 * the flow must revert to the static pending surface, never retry silently.
 */
export function isRegistrationFinancialAuthRejection(error: unknown): boolean {
  return error instanceof RegistrationFinancialApiError && error.status === 401;
}

export interface RegistrationFinancialClient {
  /** `options.signal` lets the poll loop abort a request that outlives the
   * bounded verification window (the deadline is raced against every
   * in-flight request). */
  status(
    handoffId: string,
    options?: { signal?: AbortSignal },
  ): Promise<RegistrationFinancialStatus>;
  getReceiptCopy(handoffId: string): Promise<ReceiptCopyPreference>;
  setReceiptCopy(handoffId: string, email: string): Promise<ReceiptCopySetResult>;
  clearReceiptCopy(handoffId: string): Promise<ReceiptCopyClearResult>;
}

export class HttpRegistrationFinancialClient implements RegistrationFinancialClient {
  private readonly fetcher: typeof fetch;

  constructor(fetcher: typeof fetch = globalThis.fetch) {
    // Bind so native fetch is not invoked with the client instance as `this`
    // (same "Illegal invocation" hazard handled in HttpAuthClient).
    this.fetcher = fetcher === globalThis.fetch ? fetcher.bind(globalThis) : fetcher;
  }

  async status(
    handoffId: string,
    options?: { signal?: AbortSignal },
  ): Promise<RegistrationFinancialStatus> {
    const response = await this.request("/registration-financial/status", handoffId, {
      method: "GET",
      ...(options?.signal !== undefined && { signal: options.signal }),
    });
    if (!response.ok) {
      throw new RegistrationFinancialApiError(
        `Registration financial status request failed with status ${response.status}.`,
        response.status,
      );
    }
    const status = parseRegistrationFinancialStatus(await response.json());
    // Fail closed on identity: a status for any other handoff is treated
    // exactly like a malformed body (clm-web#242 item 1). UUIDs compare
    // case-insensitively: the financial service canonicalizes them to
    // lowercase, so a differently cased echo is the SAME handoff.
    if (status.handoffId.toLowerCase() !== handoffId.toLowerCase()) {
      throw new RegistrationFinancialApiError("Malformed registration financial status.", 0);
    }
    return status;
  }

  async getReceiptCopy(handoffId: string): Promise<ReceiptCopyPreference> {
    const response = await this.request("/registration-financial/receipt-copy", handoffId, {
      method: "GET",
    });
    if (!response.ok) {
      throw new RegistrationFinancialApiError(
        `Receipt-copy read failed with status ${response.status}.`,
        response.status,
      );
    }
    return parseReceiptCopyPreference(await response.json());
  }

  async setReceiptCopy(handoffId: string, email: string): Promise<ReceiptCopySetResult> {
    const response = await this.request("/registration-financial/receipt-copy", handoffId, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (response.ok) {
      return { outcome: "saved", preference: parseReceiptCopyPreference(await response.json()) };
    }
    // Server validation is authoritative; its rejection categories map to
    // fixed user-safe copy (no server internals rendered).
    if (response.status === 400 || response.status === 422) {
      return { outcome: "rejected", reason: "invalid_address" };
    }
    if (response.status === 409) {
      return { outcome: "rejected", reason: "no_longer_editable" };
    }
    throw new RegistrationFinancialApiError(
      `Receipt-copy update failed with status ${response.status}.`,
      response.status,
    );
  }

  async clearReceiptCopy(handoffId: string): Promise<ReceiptCopyClearResult> {
    const response = await this.request("/registration-financial/receipt-copy", handoffId, {
      method: "DELETE",
    });
    if (response.ok) {
      return { outcome: "cleared" };
    }
    if (response.status === 409) {
      return { outcome: "rejected", reason: "no_longer_editable" };
    }
    throw new RegistrationFinancialApiError(
      `Receipt-copy clear failed with status ${response.status}.`,
      response.status,
    );
  }

  private async request(
    path: `/${string}`,
    handoffId: string,
    init: { method: string; headers?: Record<string, string>; body?: string; signal?: AbortSignal },
  ): Promise<Response> {
    // FR-046: no held handoff-scoped token → never call the API. Surfaced as
    // the same 401-shaped rejection an expired token produces, so the flow
    // converges on the static pending fallback.
    const accessToken = readRegistrationFinancialToken(handoffId);
    if (accessToken === null) {
      throw new RegistrationFinancialApiError(
        "No registration financial access token is held.",
        401,
      );
    }
    // Token-authenticated financial-service call (FR-046): the handoffId
    // query parameter is an opaque server-issued UUID on an authenticated
    // API URL — never a public bearer URL, and never a person/company
    // identifier. The bearer travels only in the Authorization header.
    const url = new URL(path, `${financialBaseUrl()}/`);
    url.searchParams.set("handoffId", handoffId);
    const response = await this.fetcher(url, {
      // Bearer-only auth: cookies MUST NOT ride along. The financial
      // service's cross-origin CORS policy is deliberately non-credentialed
      // (allow_credentials=False) — credentialed requests would have their
      // responses withheld by the browser.
      credentials: "omit",
      method: init.method,
      ...(init.body !== undefined && { body: init.body }),
      ...(init.signal !== undefined && { signal: init.signal }),
      headers: { ...init.headers, Authorization: `Bearer ${accessToken}` },
    });
    if (response.status === 401) {
      // FR-046 expiry UX: the token is no longer honored — scrub it so the
      // flow reverts to the static pending surface (no silent refresh loop).
      clearRegistrationFinancialToken(handoffId);
    }
    return response;
  }
}

function financialBaseUrl(): string {
  const baseUrl = getRuntimeConfig().apiBaseUrls.financial;
  if (!baseUrl) {
    throw new RegistrationFinancialApiError("Runtime config is missing apiBaseUrls.financial.", 0);
  }
  return baseUrl.replace(/\/$/, "");
}

/**
 * Boundary parser for the status payload. Unknown states or malformed shapes
 * throw (fail closed into the caller's transient-error handling) instead of
 * leaking unvalidated data into rendering decisions.
 */
export function parseRegistrationFinancialStatus(payload: unknown): RegistrationFinancialStatus {
  if (!isRecord(payload)) {
    throw new RegistrationFinancialApiError("Malformed registration financial status.", 0);
  }

  const { handoffId, state, outcomeCategory, payment, supportCorrelation } = payload;

  if (typeof handoffId !== "string" || !isFinancialState(state)) {
    throw new RegistrationFinancialApiError("Malformed registration financial status.", 0);
  }

  return {
    handoffId,
    state,
    outcomeCategory: isOutcomeCategory(outcomeCategory) ? outcomeCategory : null,
    payment: parsePaymentOffer(payment),
    supportCorrelation: typeof supportCorrelation === "string" ? supportCorrelation : null,
  };
}

function parsePaymentOffer(value: unknown): RegistrationFinancialPaymentOffer | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (
    !isRecord(value) ||
    typeof value.amountDue !== "string" ||
    (value.processing !== undefined && typeof value.processing !== "boolean") ||
    typeof value.retryAvailable !== "boolean"
  ) {
    // retryAvailable is schema-required; processing is an optional
    // provider-pending discriminator omitted by the current producer.
    // Reject malformed fields without coercion; only absent processing
    // defaults to false below so valid checkout offers remain available.
    return null;
  }
  const secret = value.paymentIntentClientSecret;
  return {
    amountDue: value.amountDue,
    paymentIntentClientSecret: typeof secret === "string" && secret.length > 0 ? secret : null,
    processing: value.processing ?? false,
    retryAvailable: value.retryAvailable,
  };
}

function parseReceiptCopyPreference(payload: unknown): ReceiptCopyPreference {
  if (!isRecord(payload)) {
    return { email: null };
  }
  return { email: typeof payload.email === "string" ? payload.email : null };
}

function isFinancialState(value: unknown): value is RegistrationFinancialState {
  return (
    typeof value === "string" &&
    (REGISTRATION_FINANCIAL_STATES as readonly string[]).includes(value)
  );
}

function isOutcomeCategory(value: unknown): value is RegistrationFinancialOutcomeCategory {
  return (
    typeof value === "string" &&
    (REGISTRATION_FINANCIAL_OUTCOME_CATEGORIES as readonly string[]).includes(value)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
